import type { Flight, FlightLeg, QuotePassenger } from '../types';
import { passengerTicketRows } from './passengers';
import { routeSummaryLabel, flightSummaryLabel } from './roundTrip';
import { SKYLUX_MAIL_DETAIL_STYLES } from './skyluxMailDetailStyles';

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function nl2brEscaped(s: string): string {
  return escapeHtml(s).replace(/\r\n/g, '\n').replace(/\n/g, '<br>\n');
}

export function firstNameFromFullName(name: string): string {
  const t = name.trim();
  if (!t) return 'Traveler';
  return t.split(/\s+/)[0] ?? t;
}

function hrefAttr(url: string): string {
  return escapeHtml(url);
}

/** Hosted brand mark for client email headers (SVG). */
const SKYLUX_EMAIL_HEADER_LOGO_URL =
  'https://attachment-storage.skyluxtravel.com/92/31/d73df10503c451c2143f2abc51f2f091.svg';

function skyluxPurpleHeaderHtml(): string {
  const logoSrc = hrefAttr(SKYLUX_EMAIL_HEADER_LOGO_URL);
  return `<div dir="ltr" class="gmail_attr">
<table border="0" cellpadding="0" cellspacing="0" width="100%" style="min-width:100%;min-height:56px;background-color:rgb(76,36,172)"><tbody><tr><td align="center">
<table align="center" cellpadding="0" cellspacing="0" style="min-width:608px;width:608px;font-family:Arial,sans-serif;font-size:14px;border-collapse:collapse;color:rgb(113,113,113)"><tbody><tr><td>
<table align="left" border="0" cellpadding="0" cellspacing="0" width="auto" style="min-width:35%;padding-left:10px"><tbody><tr><td style="padding:10px 0;vertical-align:middle">
<img src="${logoSrc}" alt="SkyLux Travel" width="280" height="48" style="display:block;max-width:280px;width:100%;height:auto;border:0;outline:none;line-height:0;font-size:0" />
</td></tr></tbody></table>
<table align="right" border="0" cellpadding="0" cellspacing="0" width="250px" style="min-width:20%"><tbody><tr>
<td style="padding:10px;text-align:right;font-size:12px;line-height:18px;font-family:Arial;color:rgb(255,255,255)" valign="bottom">
<span style="font-family:Arial">(USA/Canada) <span style="text-transform:uppercase">
<a href="tel:+18889852261" style="text-decoration:none;line-height:18px;font-family:Arial;color:rgb(255,255,255)" target="_blank" rel="noopener">(888) 985-2261</a>
</span></span>
</td></tr></tbody></table>
</td></tr></tbody></table>
</td></tr></tbody></table>
</div>`;
}

function skyluxFooterBlockHtml(): string {
  return ` 
   <table width="100%" cellspacing="0" cellpadding="0" border="0" bgcolor="#ffffff" style="min-width:100%">
    <tbody><tr>
        <td align="center" style="padding:8px 0px 14px;border-top:1px solid rgb(225,228,231);max-width:608px;width:608px">
            <table width="100%" cellspacing="0" cellpadding="0" align="center" style="max-width:608px;width:608px;font-family:Arial,sans-serif;font-size:12px;border-collapse:collapse">
                <tbody style="font-family:Arial,sans-serif"><tr style="font-family:Arial,sans-serif">
                    <td style="font-family:Arial,sans-serif" align="center">
                        <table cellspacing="0" cellpadding="0" style="font-family:Arial,sans-serif;font-size:12px;border-collapse:collapse">
                            <tbody style="font-family:Arial,sans-serif"><tr style="font-family:Arial,sans-serif">
                                <td style="padding:4px 32px 0px 0px;text-align:left;font-family:Arial,sans-serif">
                            <span style="line-height:16px;font-family:Arial,sans-serif"><b style="font-family:Arial,sans-serif">Direct Number</b></span><br>
                            <span style="line-height:16px;font-family:Arial,sans-serif">USA, Canada</span>
                                </td>
                                <td style="padding:4px 3px 0px;font-family:Arial,sans-serif">
                            <a target="_blank" style="font-family:Arial,sans-serif" href="tel:(888)%20985-2261" rel="noopener">
                            <table style="display:inline-block;background:0% 0% repeat rgb(74,37,172);font-size:12px;font-family:Arial,sans-serif;color:rgb(255,255,255)" cellpadding="0" cellspacing="0">
                                <tbody style="font-family:Arial,sans-serif"><tr style="font-family:Arial,sans-serif">
                                    <td style="text-align:center;height:47px;width:166px;font-family:Arial,sans-serif">
                                        <b style="font-family:Arial,sans-serif">(888) 985-2261</b>
                                    </td>
                                </tr>
                            </tbody></table>    
                            </a>
                                </td>
                            </tr>
                        </tbody></table>
                    </td>
                </tr>
            </tbody></table>
        </td>
    </tr>
</tbody></table>
<table width="100%" cellspacing="0" cellpadding="0" border="0" bgcolor="#FFFFFF" style="min-width:100%">
    <tbody><tr>
        <td align="center">
            <table width="100%" cellspacing="0" cellpadding="0" align="center" style="min-width:608px;width:608px;font-family:Arial,sans-serif;font-size:14px;border-collapse:collapse;border:1px solid rgb(208,208,208);color:rgb(0,0,0)">
                <tbody style="font-family:Arial,sans-serif"><tr style="border-bottom:1px solid rgb(225,228,231);font-family:Arial,sans-serif">
                    <td align="center" valign="top" style="padding-top:20px;font-family:Arial,sans-serif">
                      <table style="font-family:Arial,sans-serif">
                        <tbody style="font-family:Arial,sans-serif"><tr style="font-family:Arial,sans-serif">
                          <td align="left" style="width:150px;font-family:Arial,sans-serif" valign="top">
                            <span style="padding:0px 0px 14px 20px;font-size:12px;line-height:22px;font-family:Arial,sans-serif;color:rgb(0,0,0)">General Lines:</span><br>
                            <span style="padding:0px 0px 14px 20px;font-size:12px;line-height:22px;font-family:Arial,sans-serif;color:rgb(0,0,0)">
                                                          </span>
                          </td>
                          <td align="right" style="width:150px;font-family:Arial,sans-serif" valign="top">
                            <span style="text-align:left;display:inline-block;vertical-align:middle;padding:0px 0px 14px;min-width:115px;font-family:Arial,sans-serif">
                            <span style="font-size:12px;line-height:22px;vertical-align:top;font-family:Arial,sans-serif">USA, Canada</span><br>
                            <span style="font-size:12px;font-family:Arial,sans-serif"><a target="_blank" style="text-decoration:none;font-family:Arial,sans-serif;color:rgb(0,0,0)" href="tel:1-855-507-9770" rel="noopener">1-855-507-9770</a></span>
                            </span>
                          </td>
                        </tr>
                      </tbody></table>
                    </td>
                </tr>
            </tbody></table>
        </td>
    </tr>
</tbody></table>
<table style="min-width:100%" width="100%" cellspacing="0" cellpadding="0" border="0" bgcolor="#ffffff">
    <tbody><tr>
        <td style="padding-bottom:20px" align="center">
            <table width="100%" cellspacing="0" cellpadding="0" align="center" style="min-width:608px;width:608px;font-family:Arial,sans-serif;font-size:14px;border-collapse:collapse;color:rgb(113,113,113)">
                <tbody style="font-family:Arial,sans-serif"><tr style="font-family:Arial,sans-serif">
                    <td style="font-family:Arial,sans-serif">
                        <table width="38%" cellspacing="0" cellpadding="0" border="0" align="left" style="min-width:38%;font-family:Arial,sans-serif">
                            <tbody style="font-family:Arial,sans-serif"><tr style="font-family:Arial,sans-serif">
                                <td style="padding:22px 0px 15px;font-family:Arial,sans-serif">
                                    <span style="font-size:10px;line-height:18px;font-family:Arial,sans-serif;color:rgb(131,127,127)">SkyLuxTravel.com</span><br>
                                    <span style="font-size:10px;line-height:18px;font-family:Arial,sans-serif;color:rgb(131,127,127)">All rights reserved. CST # 2119950-40</span>
                                </td>
                            </tr>
                        </tbody></table>
                      \t<table width="31%" cellspacing="0" cellpadding="0" border="0" align="left" style="min-width:31%;font-family:Arial,sans-serif">
                            <tbody style="font-family:Arial,sans-serif"><tr style="font-family:Arial,sans-serif">
                                <td valign="middle" style="padding:15px 0px 22px;font-family:Arial,sans-serif">
                                    <table style="font-family:Arial,sans-serif" cellspacing="0" cellpadding="0" align="left">
                                        <tbody style="font-family:Arial,sans-serif"><tr style="font-family:Arial,sans-serif">
                                            <td style="padding:5px 10px;font-family:Arial,sans-serif"><img style="font-family: Arial, sans-serif;" height="20px" src="https://attachment-storage.asaptickets.com/30/280/b868d3021ed045fb24fe9049cd2347c5.png"></td>
                                \t\t\t<td style="padding:5px 10px;font-family:Arial,sans-serif"><img style="font-family: Arial, sans-serif;" height="20px" src="https://attachment-storage.asaptickets.com/30/280/1edd571ed421425c7ec33299cf745b49.png"></td>
                                        </tr>
                                    </tbody></table>
                                </td>
                            </tr>
                        </tbody></table>
                        <table width="31%" cellspacing="0" cellpadding="0" border="0" align="right" style="min-width:31%;font-family:Arial,sans-serif">
                            <tbody style="font-family:Arial,sans-serif"><tr style="font-family:Arial,sans-serif">
                                <td valign="middle" style="padding:15px 0px 22px;font-family:Arial,sans-serif">
                                    <table style="font-family:Arial,sans-serif" cellspacing="0" cellpadding="0" align="right">
                                        <tbody style="font-family:Arial,sans-serif"><tr style="font-family:Arial,sans-serif">
                                            <td style="padding:0px 4px 0px 0px;font-family:Arial,sans-serif">
                                                <span style="width:40px;height:35px;padding:5px 0px 0px;display:block;text-align:center;vertical-align:top;font-family:Arial,sans-serif">
                                                    <a target="_blank" style="font-family:Arial,sans-serif" valign="middle" href="https://twitter.com/skyluxtravel" rel="noopener"><img style="font-family: Arial, sans-serif;" height="20" src="https://attachment-storage.asaptickets.com/30/280/5bd8debe9b8de938d3b81f537d9f3071.png"></a>
                                                </span>
                                            </td>
                                            <td style="padding:0px 4px;font-family:Arial,sans-serif">
                                                <span style="width:40px;height:35px;padding:5px 0px 0px;display:block;text-align:center;vertical-align:top;font-family:Arial,sans-serif">
                                                   <a target="_blank" style="font-family:Arial,sans-serif" valign="middle" href="https://www.facebook.com/skyluxtravel" rel="noopener"><img style="font-family: Arial, sans-serif;" height="20" src="https://attachment-storage.asaptickets.com/30/280/085ca911682d55eb3780b93db4e15966.png"></a>
                                                </span>
                                            </td>
                                            <td style="padding:0px 4px;font-family:Arial,sans-serif">
                                                <span style="width:40px;height:34px;padding:6px 0px 0px;display:block;text-align:center;vertical-align:top;font-family:Arial,sans-serif">
                                                  <a target="_blank" style="font-family:Arial,sans-serif" height="30" valign="middle" href="https://www.linkedin.com/company/skylux-travel?trk=company_name" rel="noopener"><img style="font-family: Arial, sans-serif;" height="20" src="https://attachment-storage.asaptickets.com/30/280/38e71e0142977a62d35a4d749e21bbae.png"></a>
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody></table>
                                </td>
                            </tr>
                        </tbody></table>
                    </td>
                </tr>
            </tbody></table>
        </td>
    </tr>    
</tbody></table>
   `;
}

function ctaRowHtml(primaryLabel: string, primaryHref: string, secondaryLabel: string, secondaryHref: string): string {
  const p = escapeHtml(primaryLabel);
  const s = escapeHtml(secondaryLabel);
  return `<table align="center" cellpadding="0" cellspacing="0" style="text-align:center;min-width:608px;width:608px;font-family:Arial,sans-serif;font-size:14px;margin-bottom:30px;color:rgb(113,113,113)"><tbody><tr>
<td style="padding:20px;font-family:Arial,sans-serif" height="58" bgcolor="#ffffff" valign="middle" width="25%">
<table cellspacing="0" cellpadding="0" style="display:inline-block;background:rgb(74,37,172);font-size:12px;margin:12px;font-family:Arial,sans-serif;color:rgb(255,255,255)"><tbody><tr>
<td style="text-align:center;height:47px;width:171px;margin:12px;font-family:Arial,sans-serif">
<a style="text-decoration:none;display:block;height:100%;line-height:47px;font-family:Arial,sans-serif;color:rgb(255,255,255)" href="${hrefAttr(primaryHref)}" target="_blank" rel="noopener"><b>${p}</b></a>
</td></tr></tbody></table>
<table cellspacing="0" cellpadding="0" style="display:inline-block;font-size:12px;border:2px solid rgb(74,37,172);margin:12px;font-family:Arial,sans-serif;color:rgb(74,37,172)"><tbody><tr>
<td style="text-align:center;height:43px;width:171px;font-family:Arial,sans-serif">
<a style="text-decoration:none;display:block;height:100%;line-height:43px;font-family:Arial,sans-serif;color:rgb(74,37,172)" href="${hrefAttr(secondaryHref)}" target="_blank" rel="noopener"><b>${s}</b></a>
</td></tr></tbody></table>
</td></tr></tbody></table>`;
}

export function wrapSkyluxClientMailDocument(
  innerHtml: string,
  ariaLabel: string,
  documentTitle = 'SkyLux Travel',
): string {
  const safeAria = escapeHtml(ariaLabel);
  const safeTitle = escapeHtml(documentTitle);
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${safeTitle}</title>
<style>${SKYLUX_MAIL_DETAIL_STYLES}</style>
</head>
<body class="mail-detail-content noI18n colorQuoted" role="complementary" aria-label="${safeAria}">
<div><br>
<div class="gmail_quote gmail_quote_container">
${innerHtml}
</div>
</div>
</body>
</html>`;
}

function appendLegPlain(lines: string[], leg: FlightLeg, heading?: string) {
  if (heading) {
    lines.push('');
    lines.push(heading);
  }
  lines.push(`${leg.airline} ${leg.flightNumber} · ${leg.departure} -> ${leg.arrival}`);
  lines.push(`Times: ${leg.departureTime} - ${leg.arrivalTime}`);
  if (typeof leg.totalDuration === 'number') lines.push(`Total duration: ${leg.totalDuration} min`);
  if (typeof leg.stopsCount === 'number') lines.push(`Stops: ${leg.stopsCount}`);
  if (leg.travelDate) lines.push(`Travel date: ${leg.travelDate}`);
  if (typeof leg.referencePrice === 'number' && leg.referencePrice > 0) {
    lines.push(`Reference fare: $${leg.referencePrice} per person`);
  }
  if (leg.segments && leg.segments.length > 0) {
    lines.push('');
    lines.push('Segments:');
    for (const s of leg.segments) {
      lines.push(
        `${s.airline} ${s.flightNumber} · ${s.departure} (${s.departureTime}) -> ${s.arrival} (${s.arrivalTime})`,
      );
      const meta: string[] = [];
      if (typeof s.duration === 'number') meta.push(`Duration: ${s.duration} min`);
      if (s.airplane) meta.push(`Aircraft: ${s.airplane}`);
      if (s.travelClass) meta.push(`Class: ${s.travelClass}`);
      if (meta.length) lines.push(`  ${meta.join(' · ')}`);
    }
  }
  if (leg.layovers && leg.layovers.length > 0) {
    lines.push('');
    lines.push('Layovers:');
    for (const l of leg.layovers) {
      lines.push(`${l.name} (${l.id}) — ${l.duration} min${l.overnight ? ', overnight' : ''}`);
    }
  }
}

function appendLegHtml(parts: string[], leg: FlightLeg, heading?: string) {
  if (heading) {
    parts.push(`<p style="margin:16px 0 8px;font-weight:700;font-size:13px">${escapeHtml(heading)}</p>`);
  }
  parts.push(
    `<p style="margin:0 0 8px;font-weight:700">${escapeHtml(`${leg.airline} ${leg.flightNumber} · ${leg.departure} -> ${leg.arrival}`)}</p>`,
  );
  parts.push(`<p style="margin:0 0 4px">${escapeHtml(`Times: ${leg.departureTime} - ${leg.arrivalTime}`)}</p>`);
  if (typeof leg.totalDuration === 'number') {
    parts.push(`<p style="margin:0 0 4px">${escapeHtml(`Total duration: ${leg.totalDuration} min`)}</p>`);
  }
  if (typeof leg.stopsCount === 'number') {
    parts.push(`<p style="margin:0 0 8px">${escapeHtml(`Stops: ${leg.stopsCount}`)}</p>`);
  }
  if (leg.travelDate) {
    parts.push(`<p style="margin:0 0 8px">${escapeHtml(`Travel date: ${leg.travelDate}`)}</p>`);
  }
  if (typeof leg.referencePrice === 'number' && leg.referencePrice > 0) {
    parts.push(
      `<p style="margin:0 0 8px">${escapeHtml(`Reference fare: $${leg.referencePrice} per person`)}</p>`,
    );
  }
  if (leg.segments && leg.segments.length > 0) {
    parts.push(
      `<p style="margin:12px 0 6px;font-weight:700">${escapeHtml('Segments')}</p><ul style="margin:0;padding-left:18px">`,
    );
    for (const s of leg.segments) {
      const line1 = escapeHtml(
        `${s.airline} ${s.flightNumber} · ${s.departure} (${s.departureTime}) -> ${s.arrival} (${s.arrivalTime})`,
      );
      const meta: string[] = [];
      if (typeof s.duration === 'number') meta.push(`Duration: ${s.duration} min`);
      if (s.airplane) meta.push(`Aircraft: ${s.airplane}`);
      if (s.travelClass) meta.push(`Class: ${s.travelClass}`);
      const line2 = meta.length ? escapeHtml(meta.join(' · ')) : '';
      parts.push(
        `<li style="margin-bottom:8px"><span style="display:block">${line1}</span>${
          line2
            ? `<span style="display:block;color:rgb(100,116,139);font-size:11px;margin-top:2px">${line2}</span>`
            : ''
        }</li>`,
      );
    }
    parts.push('</ul>');
  }
  if (leg.layovers && leg.layovers.length > 0) {
    parts.push(
      `<p style="margin:12px 0 6px;font-weight:700">${escapeHtml('Layovers')}</p><ul style="margin:0;padding-left:18px">`,
    );
    for (const l of leg.layovers) {
      parts.push(
        `<li style="margin-bottom:4px">${escapeHtml(`${l.name} (${l.id}) — ${l.duration} min${l.overnight ? ', overnight' : ''}`)}</li>`,
      );
    }
    parts.push('</ul>');
  }
}

function appendPassengersPlain(lines: string[], passengers?: QuotePassenger[]) {
  const list = passengers?.length ? passengers : [];
  if (list.length === 0) return;
  lines.push('');
  lines.push('Passengers:');
  list.forEach((p, i) => {
    const agePart = p.age != null ? `, age ${p.age}` : '';
    lines.push(`  ${i + 1}. ${p.name.toUpperCase()}${agePart}`);
  });
}

/** Plain-text block for ticket bodies, email previews, and plain multipart mail. */
export function formatFlightQuoteDetailsPlain(
  f: Flight,
  quotedPerPerson: number,
  passengers?: QuotePassenger[],
): string {
  const lines: string[] = [];
  appendPassengersPlain(lines, passengers);
  appendLegPlain(lines, f, f.returnLeg ? 'Outbound' : undefined);
  if (f.returnLeg) {
    const retHeading = f.returnDate ? `Return (${f.returnDate})` : 'Return';
    appendLegPlain(lines, f.returnLeg, retHeading);
    const outRef = f.outboundReferencePrice ?? f.price - (f.returnLeg.referencePrice ?? 0);
    const retRef = f.returnLeg.referencePrice ?? 0;
    lines.push(
      `Round-trip reference: $${f.price} per person ($${outRef} outbound + $${retRef} return)`,
    );
  } else {
    lines.push(`Listed reference fare: $${f.price} per person`);
  }
  lines.push(`Your quoted price: $${quotedPerPerson} per person`);
  return lines.join('\n');
}

/** Short summary for quote email plain-text (no segment-level itinerary). */
export function formatFlightQuoteSummaryPlain(f: Flight, quotedPerPerson: number): string {
  return [`Route: ${routeSummaryLabel(f)}`, `Quoted price: $${quotedPerPerson} per person`].join('\n');
}

/** HTML fragment (already escaped) for quote client email. */
export function formatFlightQuoteDetailsHtml(
  f: Flight,
  quotedPerPerson: number,
  passengers?: QuotePassenger[],
): string {
  const parts: string[] = [];
  const list = passengers?.length ? passengers : [];
  if (list.length > 0) {
    parts.push(`<p style="margin:0 0 8px;font-weight:700">${escapeHtml('Passengers')}</p><ul style="margin:0 0 12px;padding-left:18px">`);
    for (const p of list) {
      const agePart = p.age != null ? ` · age ${p.age}` : '';
      parts.push(`<li style="margin-bottom:4px">${escapeHtml(p.name.toUpperCase())}${escapeHtml(agePart)}</li>`);
    }
    parts.push('</ul>');
  }
  appendLegHtml(parts, f, f.returnLeg ? 'Outbound' : undefined);
  if (f.returnLeg) {
    const retHeading = f.returnDate ? `Return (${f.returnDate})` : 'Return';
    appendLegHtml(parts, f.returnLeg, retHeading);
    parts.push(
      `<p style="margin:12px 0 4px;font-weight:700">${escapeHtml(
        `Round-trip reference: $${f.price} per person ($${f.outboundReferencePrice ?? f.price - (f.returnLeg.referencePrice ?? 0)} outbound + $${f.returnLeg.referencePrice ?? 0} return)`,
      )}</p>`,
    );
  } else {
    parts.push(`<p style="margin:0 0 4px">${escapeHtml(`Listed reference fare: $${f.price} per person`)}</p>`);
  }
  parts.push(`<p style="margin:0 0 8px;font-weight:700">${escapeHtml(`Your quoted price: $${quotedPerPerson} per person`)}</p>`);
  return parts.join('');
}

export type SkyluxQuoteMailParams = {
  clientName: string;
  introText: string;
  flight: Flight;
  pricePerPerson: number;
  bookingUrl: string;
  paymentUrl: string;
  passengers?: QuotePassenger[];
};

export function buildSkyluxQuoteClientMailHtml(p: SkyluxQuoteMailParams): string {
  const first = firstNameFromFullName(p.clientName);
  const intro = nl2brEscaped(p.introText);
  const f = p.flight;
  const flight = escapeHtml(flightSummaryLabel(f));
  const route = escapeHtml(routeSummaryLabel(f));
  const price = escapeHtml(`$${p.pricePerPerson} per person`);
  const aria = f.returnLeg
    ? `SkyLux Travel round-trip quote. ${f.departure} to ${f.arrival} and return.`
    : `SkyLux Travel flight quote. ${f.departure} to ${f.arrival}.`;
  const body = `${skyluxPurpleHeaderHtml()}
<div class="gmail_quote"><div><div style="margin:0;padding:0;min-width:100%!important">
<table border="0" cellpadding="0" cellspacing="0" width="100%" bgcolor="#ffffff" style="min-width:100%"><tbody><tr>
<td align="center" style="padding:20px 0 18px">
<table align="center" width="100%" cellpadding="0" cellspacing="0" style="min-width:608px;width:608px;font-family:Arial,sans-serif;font-size:14px;border-collapse:collapse;color:rgb(113,113,113)"><tbody><tr>
<td style="padding:0 20px;font-family:Arial,sans-serif">
<table border="0" cellpadding="0" cellspacing="0" width="100%" style="min-width:100%;font-family:Arial,sans-serif" align="left"><tbody><tr>
<td style="font-family:Arial,sans-serif">
<span style="text-align:center;display:block;font-size:19px;padding:6px 0 10px;font-family:Arial,sans-serif;color:rgb(52,73,94)">Your flight quote</span>
</td></tr></tbody></table>
</td></tr></tbody></table>
</td></tr></tbody></table>
<table border="0" cellpadding="0" cellspacing="0" bgcolor="#ffffff" width="100%" style="min-width:100%;font-family:Arial,sans-serif"><tbody><tr>
<td align="center" style="font-family:Arial,sans-serif">
<table align="center" cellpadding="0" cellspacing="0" style="min-width:608px;width:608px;font-family:Arial,sans-serif;font-size:14px;border-collapse:collapse;color:rgb(113,113,113)"><tbody><tr>
<td style="font-size:12px;display:block;padding:20px;line-height:1.3;font-family:Arial,sans-serif;color:rgb(52,73,94)">
<div style="text-align:center;font-family:Arial,sans-serif">
Dear ${escapeHtml(first)},<br><br>
${intro}<br><br>
</div>
</td></tr></tbody></table>
<table style="min-width:608px;width:608px;padding:20px;font-family:Arial,sans-serif" align="center" cellpadding="0" cellspacing="0" width="100%"><tbody>
<tr valign="middle" height="15" style="font-size:10px;line-height:20px;font-family:Arial;text-align:center;color:rgb(131,127,127)">
<td width="50%" style="border-top:1px solid rgb(198,211,222);font-family:Arial"><span style="font-family:Arial">Flight</span></td>
<td width="50%" style="border-left:1px solid rgb(198,211,222);border-top:1px solid rgb(198,211,222);font-family:Arial"><span style="font-family:Arial">Route</span></td>
</tr>
<tr valign="middle" height="30" style="font-size:16px;line-height:16px;font-family:Arial;text-align:center;color:rgb(0,0,0)">
<td width="50%" style="border-bottom:1px solid rgb(198,211,222);font-family:Arial"><span style="line-height:25px;font-family:Arial">${flight}</span></td>
<td width="50%" style="border-left:1px solid rgb(198,211,222);border-bottom:1px solid rgb(198,211,222);font-family:Arial"><span style="line-height:25px;font-family:Arial">${route}</span></td>
</tr>
<tr valign="middle" height="15" style="font-size:10px;line-height:20px;font-family:Arial;text-align:center;color:rgb(131,127,127)">
<td width="50%" style="border-top:1px solid rgb(198,211,222);font-family:Arial"><span style="font-family:Arial">Quoted fare</span></td>
<td width="50%" style="border-left:1px solid rgb(198,211,222);border-top:1px solid rgb(198,211,222);font-family:Arial"><span style="font-family:Arial">Per person</span></td>
</tr>
<tr valign="middle" height="30" style="font-size:16px;line-height:16px;font-family:Arial;text-align:center;color:rgb(0,0,0)">
<td width="50%" style="border-bottom:1px solid rgb(198,211,222);font-family:Arial"><span style="line-height:25px;font-family:Arial">${price}</span></td>
<td width="50%" style="border-left:1px solid rgb(198,211,222);border-bottom:1px solid rgb(198,211,222);font-family:Arial"><span style="line-height:25px;font-family:Arial">USD</span></td>
</tr>
</tbody></table>
<p style="margin:0 20px 12px;padding:0 20px;font-family:Arial,sans-serif;font-size:11px;line-height:1.45;color:rgb(100,116,139);text-align:center">
Full itinerary and flight details are on <strong>Review &amp; book</strong>.
</p>
<table align="center" cellpadding="0" cellspacing="0" style="min-width:608px;width:608px;font-family:Arial,sans-serif;font-size:14px;border-collapse:collapse;color:rgb(113,113,113)"><tbody><tr>
<td style="font-family:Arial,sans-serif">
${ctaRowHtml('Review & book', p.bookingUrl, 'Complete payment', p.paymentUrl)}
</td></tr>
<tr><td style="padding:0 20px 20px;font-family:Arial,sans-serif;color:rgb(52,73,94)">
<span style="font-size:16px;line-height:24px;display:block;font-weight:700;padding-bottom:10px;font-family:Arial,sans-serif;color:rgb(52,73,94)">Next steps</span>
<span style="line-height:18px;font-size:12px;font-family:Arial,sans-serif">Use <b>Review &amp; book</b> to confirm details, or <b>Complete payment</b> when you are ready to pay. If you have questions, reply to this email or call us at (888) 985-2261.</span>
</td></tr></tbody></table>
</td></tr></tbody></table>
${skyluxFooterBlockHtml()}
</div></div></div>`;

  return wrapSkyluxClientMailDocument(body, aria, 'SkyLux Travel — Your flight quote');
}

/** Suggested check-in line (e-ticket style; ~3h before departure when date+time parse). */
export function formatSuggestedCheckInLine(f: Flight): string {
  const outbound = formatLegCheckInLine(f.travelDate, f.departureTime);
  if (f.returnLeg) {
    const inbound = formatLegCheckInLine(f.returnLeg.travelDate ?? f.returnDate, f.returnLeg.departureTime);
    return `Outbound: ${outbound}. Return: ${inbound}.`;
  }
  return outbound;
}

function formatLegDateTimeLine(travelDate?: string, departureTime?: string, hoursBefore = 0): string {
  const d = travelDate?.trim();
  const t = departureTime?.trim();
  if (d && t) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
    const tm = /^(\d{1,2}):(\d{2})$/.exec(t);
    if (m && tm) {
      const y = Number(m[1]);
      const mo = Number(m[2]) - 1;
      const day = Number(m[3]);
      const hh = Number(tm[1]);
      const mm = Number(tm[2]);
      const depart = new Date(y, mo, day, hh, mm, 0, 0);
      if (!Number.isNaN(depart.getTime())) {
        const when = new Date(depart.getTime());
        if (hoursBefore) when.setHours(when.getHours() - hoursBefore);
        return when.toLocaleString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        });
      }
    }
    return `${d} at ${t} (local time)`;
  }
  if (t) return `Departure ${t} — please confirm with your airline.`;
  return 'Please confirm departure time with your airline.';
}

function formatLegCheckInLine(travelDate?: string, departureTime?: string): string {
  return formatLegDateTimeLine(travelDate, departureTime, 3);
}

/** Outbound departure for quote e-ticket (e.g. Saturday, May 16, 2026 at 6:10 PM). */
export function formatFlightDepartureDisplayLine(f: Flight): string {
  const line = formatLegDateTimeLine(f.travelDate, f.departureTime, 0);
  return line.endsWith('.') ? line : `${line}.`;
}

export function buildPassengerTicketTableHtml(rows: { name: string; eTicketNumber: string }[]): string {
  const dataRows = rows
    .map((row) => {
      const cellBorder = 'border-bottom:1px solid rgb(198,211,222);';
      const name = escapeHtml(row.name);
      const ticket = escapeHtml(row.eTicketNumber);
      return `<tr valign="middle" height="30" style="font-size:14px;line-height:16px;font-family:Arial;text-align:center;color:rgb(0,0,0)">
<td width="50%" style="${cellBorder}font-family:Arial"><span style="line-height:22px;font-family:Arial;text-transform:uppercase">${name}</span></td>
<td width="50%" style="border-left:1px solid rgb(198,211,222);${cellBorder}font-family:Arial"><span style="line-height:22px;font-family:Arial;letter-spacing:0.02em">${ticket}</span></td>
</tr>`;
    })
    .join('');
  return `<table width="100%" cellspacing="0" cellpadding="0" align="center" style="min-width:608px;width:608px;padding:0 20px 12px;font-family:Arial,sans-serif"><tbody>
<tr valign="middle" height="15" style="font-size:10px;line-height:20px;font-family:Arial;text-align:center;color:rgb(131,127,127)">
<td width="50%" style="border-top:1px solid rgb(198,211,222);font-family:Arial"><span style="font-family:Arial">Passenger name</span></td>
<td width="50%" style="border-left:1px solid rgb(198,211,222);border-top:1px solid rgb(198,211,222);font-family:Arial"><span style="font-family:Arial">Ticket number</span></td>
</tr>
${dataRows}
</tbody></table>`;
}

const eticketPolicyBlockStyle =
  'padding:0 20px 14px;font-family:Arial,sans-serif;font-size:11px;line-height:1.45;color:rgb(52,73,94)';

function eticketLegalSectionsHtml(params: {
  airline: string;
  signatureName: string;
  signatureEmail: string;
}): string {
  const airline = escapeHtml(params.airline);
  const baggageHref = `https://www.google.com/search?q=${encodeURIComponent(`${params.airline} baggage allowance fees`)}`;
  const sigName = escapeHtml(params.signatureName.trim());
  const sigEmail = escapeHtml(params.signatureEmail.trim());
  const mailto = `mailto:${params.signatureEmail.trim()}`;
  const iataPrivacy = 'https://www.iatatravelcenter.com/privacy';

  const p = (t: string) =>
    `<p style="margin:0 0 10px;font-family:Arial,sans-serif;font-size:11px;line-height:1.45;color:rgb(52,73,94)">${t}</p>`;

  return `<table align="center" cellpadding="0" cellspacing="0" style="min-width:608px;width:608px;font-family:Arial,sans-serif;border-collapse:collapse;color:rgb(113,113,113)"><tbody>
<tr><td style="${eticketPolicyBlockStyle}">
<span style="font-size:13px;font-weight:700;display:block;margin-bottom:8px;font-family:Arial,sans-serif;color:rgb(52,73,94)">Baggage</span>
${p(
  `Please Note: Fees for extra airline baggage are not included and will apply. For specific baggage allowance and fees, <a href="${hrefAttr(baggageHref)}" style="color:rgb(74,37,172)" target="_blank" rel="noopener">click here for: ${airline}</a>.`,
)}
<span style="font-size:13px;font-weight:700;display:block;margin:16px 0 8px;font-family:Arial,sans-serif;color:rgb(52,73,94)">Please Read Carefully</span>
${p(
  'Your tickets are electronic; an e-ticket is a paperless method of ticketing flights; therefore, no tickets will be mailed to you. You can print your travel itinerary or Electronic Ticket Receipt on Airline website using your Confirmation Number. Kindly retain your itinerary for check in, passport control and possible claims.',
)}
${p(
  'NO SHOW ticket(s) will not be processed for refund and/or exchange. All no show tickets are immediately suspended by the airlines and have no value.',
)}
${p(
  'Skylux Travel has no power to reinstate tickets that have been suspended by the airline. Skylux Travel agents will not cancel any reservation without a written cancellation request from a customer. Cancellation of reservation does not automatically initiate refund. If fare rules allow exchanges and/or refunds, they can be processed only prior to scheduled departure. If you wish to exchange or refund your ticket after the scheduled departure you must obtain a written confirmation from our Customer Service Department. This confirmation may be obtained only prior to the scheduled departure. Customer Service Department will email the confirmation on request provided the fare rules allow exchanges or refunds after the scheduled departure. If you are unable to travel we strongly recommend that you contact our client services department well in advance in order to determine whether the fare allows exchanges or refunds after scheduled departure. Please keep in mind that our regular business hours are Monday through Friday 8 am to 6 pm PST. We reserve the right to receive at least 48 hour advance notice for any exchange requests. We would like to advise you that at this time the flight departure and arrival times are correct. However, airlines frequently change times and/or flight numbers. We are not responsible for schedule changes. Please contact the airline to verify current flight information 72 hours prior to your scheduled departure. Baggage allowances and fees associated with the checked baggage vary widely; please contact the airline for specific limits and fees. Check in time recommended for all international flights is a minimum of 3 hours, even if you are traveling on a domestic carrier to another airport and connecting to your international flight. Travel documents required for all tickets (paper, e-tickets, or paperless) include a valid Passport, which must be presented for all international flights. Some countries may require a Visa and/or health card.',
)}
${p(
  'It is the passenger&apos;s responsibility to make sure you have all necessary travel documents in your possession at check-in. Please verify the visa requirements for all your stops as some of the countries may require a transit visa.',
)}
${p(
  `Those passengers transiting via the airports within the European Union - Schengen Zone may need a Schengen Entry visa. Traveling on a one way ticket may be restricted. If you are traveling on a one way ticket, it is your responsibility to make sure you are eligible. Skylux Travel agents do not advise passengers on visa requirements. Please contact the embassy of the country you are going to visit or transit through to get the up-to-date requirements. Data Protection Notice: Your personal data will be processed in accordance with the applicable carrier&apos;s privacy policy and, if your booking is made via a reservation system provider (GDS), with its privacy policy. These are available at <a href="${hrefAttr(iataPrivacy)}" style="color:rgb(74,37,172)" target="_blank" rel="noopener">www.iatatravelcenter.com/privacy</a> or from the carrier or GDS directly. You should read this documentation, which applies to your booking and specifies, for example, how your personal data is collected, stored, used, disclosed and transferred.`,
)}
<p style="margin:18px 0 0;font-family:Arial,sans-serif;font-size:11px;line-height:1.5;color:rgb(52,73,94)">
Sincerely,<br>
${sigName}<br>
<a href="${hrefAttr(mailto)}" style="color:rgb(74,37,172);font-family:Arial,sans-serif" target="_blank" rel="noopener">${sigEmail}</a>
</p>
</td></tr></tbody></table>`;
}

export type SkyluxTicketingQuoteMailParams = {
  clientName: string;
  introText: string;
  flight: Flight;
  pricePerPerson: number;
  bookingUrl: string;
  paymentUrl: string;
  passengers?: QuotePassenger[];
  /** PNR-style confirmation on the receipt (e.g. R28SSK). */
  confirmationNumber: string;
  /** Numeric-style e-ticket number for the passenger row. */
  eTicketNumber: string;
  /** Internal support desk code (TK-xxxx). */
  supportTicketCode: string;
  quoteId: string;
  signatureName: string;
  signatureEmail: string;
  /** Primary CTA label (purple button). Default: View ticket */
  primaryCtaLabel?: string;
  /** Primary CTA URL. Default: bookingUrl */
  primaryCtaUrl?: string;
  /** Secondary CTA label (outline). Default: View invoice */
  secondaryCtaLabel?: string;
  /** Secondary CTA URL. Default: paymentUrl */
  secondaryCtaUrl?: string;
  /** `quote` — passengers + departure date only; `receipt` — full e-ticket layout. */
  layout?: 'quote' | 'receipt';
};

/** E-ticket layout aligned with `aditional/ticketing.html` — filled when sending a quote. */
export function buildSkyluxTicketingQuoteMailHtml(p: SkyluxTicketingQuoteMailParams): string {
  const first = firstNameFromFullName(p.clientName);
  const intro = nl2brEscaped(p.introText);
  const f = p.flight;
  const layout = p.layout ?? 'receipt';
  const checkLine = escapeHtml(formatSuggestedCheckInLine(f));
  const departureLine = escapeHtml(formatFlightDepartureDisplayLine(f));
  const ticketRows = passengerTicketRows({
    passengers: p.passengers,
    clientSnapshot: { name: p.clientName, email: '' },
    eTicketNumber: p.eTicketNumber,
  });
  const passengerTableHtml = buildPassengerTicketTableHtml(ticketRows);
  const receiptLine = `SkyLux Travel Ticket Receipt. Confirmation number: ${p.confirmationNumber}.`;
  const primaryCtaLabel = p.primaryCtaLabel ?? 'View ticket';
  const primaryCtaUrl = p.primaryCtaUrl ?? p.bookingUrl;
  const secondaryCtaLabel = p.secondaryCtaLabel ?? 'View invoice';
  const secondaryCtaUrl = p.secondaryCtaUrl ?? p.paymentUrl;
  const aria = f.returnLeg
    ? `${receiptLine} Round trip ${f.departure} to ${f.arrival} and return. Quote: ${p.quoteId}. Support: ${p.supportTicketCode}.`
    : `${receiptLine} Quote: ${p.quoteId}. Support: ${p.supportTicketCode}. CTAs: ${primaryCtaLabel}; ${secondaryCtaLabel}.`;
  const documentTitle = `SkyLux Travel — eTicket. Confirmation number: ${p.confirmationNumber}`;

  const introBlockQuote = `<div style="text-align:center;font-family:Arial,sans-serif">
Dear ${escapeHtml(first)},<br><br>
${intro}<br><br>
${departureLine}
</div>`;

  const introBlockReceipt = `<div style="text-align:center;font-family:Arial,sans-serif">
Dear ${escapeHtml(first)},<br><br>
${intro}<br><br>
We are pleased to confirm your airline booking. It is recommended that passenger(s) check in three hours before scheduled takeoff for all flights. As of today your suggested check in time is<br>
${checkLine}.<br><br>
</div>`;

  const introBlock = layout === 'quote' ? introBlockQuote : introBlockReceipt;

  const body = `${skyluxPurpleHeaderHtml()}
<div class="gmail_quote"><div><div style="margin:0;padding:0;min-width:100%!important">
<table border="0" cellpadding="0" cellspacing="0" width="100%" bgcolor="#ffffff" style="min-width:100%"><tbody><tr>
<td align="center" style="padding:20px 0 18px">
<table align="center" width="100%" cellpadding="0" cellspacing="0" style="min-width:608px;width:608px;font-family:Arial,sans-serif;font-size:14px;border-collapse:collapse;color:rgb(113,113,113)"><tbody><tr>
<td style="padding:0 20px;font-family:Arial,sans-serif">
<span style="text-align:center;display:block;font-size:19px;padding:6px 0 4px;font-family:Arial,sans-serif;color:rgb(52,73,94)">Your E-Ticket</span>
<span style="text-align:center;display:block;font-size:12px;line-height:1.35;font-family:Arial,sans-serif;color:rgb(100,116,139)">${escapeHtml(receiptLine)}</span>
</td></tr></tbody></table>
</td></tr></tbody></table>
<table border="0" cellpadding="0" cellspacing="0" bgcolor="#ffffff" width="100%" style="min-width:100%;font-family:Arial,sans-serif"><tbody><tr>
<td align="center" style="font-family:Arial,sans-serif">
<table align="center" cellpadding="0" cellspacing="0" style="min-width:608px;width:608px;font-family:Arial,sans-serif;font-size:14px;border-collapse:collapse;color:rgb(113,113,113)"><tbody><tr>
<td style="font-size:12px;display:block;padding:20px;line-height:1.3;font-family:Arial,sans-serif;color:rgb(52,73,94)">
${introBlock}
</td></tr></tbody></table>
${passengerTableHtml}
<table align="center" cellpadding="0" cellspacing="0" style="min-width:608px;width:608px;font-family:Arial,sans-serif;font-size:14px;border-collapse:collapse;color:rgb(113,113,113)"><tbody><tr>
<td style="font-family:Arial,sans-serif">
${ctaRowHtml(primaryCtaLabel, primaryCtaUrl, secondaryCtaLabel, secondaryCtaUrl)}
</td></tr></tbody></table>
${eticketLegalSectionsHtml({
    airline: f.airline,
    signatureName: p.signatureName,
    signatureEmail: p.signatureEmail,
  })}
</td></tr></tbody></table>
${skyluxFooterBlockHtml()}
</div></div></div>`;

  return wrapSkyluxClientMailDocument(body, aria, documentTitle);
}

export type SkyluxTicketDeskMailParams = {
  code: string;
  title: string;
  description: string;
  clientName: string;
  status: string;
  priority: string;
};

export function buildSkyluxTicketDeskClientMailHtml(p: SkyluxTicketDeskMailParams): string {
  const first = firstNameFromFullName(p.clientName);
  const desc = nl2brEscaped(p.description);
  const aria = `SkyLux Travel ticket ${p.code}. ${p.title}`;

  const body = `${skyluxPurpleHeaderHtml()}
<div class="gmail_quote"><div><div style="margin:0;padding:0;min-width:100%!important">
<table border="0" cellpadding="0" cellspacing="0" width="100%" bgcolor="#ffffff" style="min-width:100%"><tbody><tr>
<td align="center" style="padding:20px 0 18px">
<table align="center" width="100%" cellpadding="0" cellspacing="0" style="min-width:608px;width:608px;font-family:Arial,sans-serif;font-size:14px;border-collapse:collapse;color:rgb(113,113,113)"><tbody><tr>
<td style="padding:0 20px;font-family:Arial,sans-serif">
<span style="text-align:center;display:block;font-size:19px;padding:6px 0 10px;font-family:Arial,sans-serif;color:rgb(52,73,94)">Your ticket update</span>
</td></tr></tbody></table>
</td></tr></tbody></table>
<table border="0" cellpadding="0" cellspacing="0" bgcolor="#ffffff" width="100%" style="min-width:100%;font-family:Arial,sans-serif"><tbody><tr>
<td align="center" style="font-family:Arial,sans-serif">
<table align="center" cellpadding="0" cellspacing="0" style="min-width:608px;width:608px;font-family:Arial,sans-serif;font-size:14px;border-collapse:collapse;color:rgb(113,113,113)"><tbody><tr>
<td style="font-size:12px;display:block;padding:20px;line-height:1.3;font-family:Arial,sans-serif;color:rgb(52,73,94)">
<div style="text-align:center;font-family:Arial,sans-serif">
Dear ${escapeHtml(first)},<br><br>
Below is the latest information for your support ticket with SkyLux Travel.<br><br>
</div>
</td></tr></tbody></table>
<table style="min-width:608px;width:608px;padding:20px;font-family:Arial,sans-serif" align="center" cellpadding="0" cellspacing="0" width="100%"><tbody>
<tr valign="middle" height="15" style="font-size:10px;line-height:20px;font-family:Arial;text-align:center;color:rgb(131,127,127)">
<td width="50%" style="border-top:1px solid rgb(198,211,222);font-family:Arial"><span style="font-family:Arial">Ticket reference</span></td>
<td width="50%" style="border-left:1px solid rgb(198,211,222);border-top:1px solid rgb(198,211,222);font-family:Arial"><span style="font-family:Arial">Subject</span></td>
</tr>
<tr valign="middle" height="30" style="font-size:14px;line-height:16px;font-family:Arial;text-align:center;color:rgb(0,0,0)">
<td width="50%" style="border-bottom:1px solid rgb(198,211,222);font-family:Arial"><span style="line-height:22px;font-family:Arial;text-transform:uppercase">${escapeHtml(p.code)}</span></td>
<td width="50%" style="border-left:1px solid rgb(198,211,222);border-bottom:1px solid rgb(198,211,222);font-family:Arial"><span style="line-height:22px;font-family:Arial">${escapeHtml(p.title)}</span></td>
</tr>
<tr valign="middle" height="15" style="font-size:10px;line-height:20px;font-family:Arial;text-align:center;color:rgb(131,127,127)">
<td width="50%" style="border-top:1px solid rgb(198,211,222);font-family:Arial"><span style="font-family:Arial">Status</span></td>
<td width="50%" style="border-left:1px solid rgb(198,211,222);border-top:1px solid rgb(198,211,222);font-family:Arial"><span style="font-family:Arial">Priority</span></td>
</tr>
<tr valign="middle" height="30" style="font-size:14px;line-height:16px;font-family:Arial;text-align:center;color:rgb(0,0,0)">
<td width="50%" style="border-bottom:1px solid rgb(198,211,222);font-family:Arial"><span style="line-height:22px;font-family:Arial">${escapeHtml(p.status)}</span></td>
<td width="50%" style="border-left:1px solid rgb(198,211,222);border-bottom:1px solid rgb(198,211,222);font-family:Arial"><span style="line-height:22px;font-family:Arial">${escapeHtml(p.priority)}</span></td>
</tr>
</tbody></table>
<table align="center" cellpadding="0" cellspacing="0" style="min-width:608px;width:608px;font-family:Arial,sans-serif;font-size:14px;border-collapse:collapse;color:rgb(113,113,113)"><tbody><tr>
<td style="padding:0 20px 20px;font-family:Arial,sans-serif;color:rgb(52,73,94)">
<span style="font-size:16px;line-height:24px;display:block;font-weight:700;padding-bottom:10px;font-family:Arial,sans-serif;color:rgb(52,73,94)">Details</span>
<span style="line-height:18px;font-size:12px;font-family:Arial,sans-serif">${desc}</span>
</td></tr></tbody></table>
</td></tr></tbody></table>
${skyluxFooterBlockHtml()}
</div></div></div>`;

  return wrapSkyluxClientMailDocument(body, aria, `SkyLux Travel — Ticket ${p.code}`);
}
